import { useEffect } from "react";
import { useZipCodeStore } from "@common/hooks";
import { FieldValues, UseFormSetValue, UseFormWatch, Path, PathValue } from "react-hook-form";

interface RHFFieldNames<T> {
	zipCode: Path<T>;
	city: Path<T>;
	county: Path<T>;
	stateId: Path<T>;
}

const getNestedValue = (obj: any, path: string): string | number => {
	return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};
/**
 * This hook updates city, county and state based on a valid zipcode.
 * IMPORTANT: It is CRUCIAL that ZipCodeStoreContextWrapper.set(new ZipCodeStore()); is set in the parent component.
 * @param watch
 * @param setValue
 * @param fieldNames
 * @param shouldDirty
 * @param shouldValidate
 */
export const useZipCodeChanged = <T extends FieldValues>(
	watch: UseFormWatch<T>,
	setValue: UseFormSetValue<T>,
	fieldNames: RHFFieldNames<T>,
	shouldDirty = true,
	shouldValidate = false
) => {
	const zipCodeStore = useZipCodeStore();
	useEffect(() => {
		// Call onZipCodeChanged when the zip code changes.
		const subscription = watch((value, { name, type }) => {
			const zipCodeField = getNestedValue(value, fieldNames.zipCode);
			if (name === fieldNames.zipCode && type === "change" && !_.isNil(zipCodeField)) zipCodeStore.fetchZipCode(zipCodeField.toString() ?? "");
		});
		return () => subscription.unsubscribe();
	}, [watch, zipCodeStore, fieldNames]);

	// If a zip code has been found by search
	// then update the form fields with the zip code listing
	useEffect(() => {
		if (zipCodeStore.zipCode) {
			setValue(fieldNames.zipCode, zipCodeStore.zipCode.zipcode as PathValue<T, Path<T>>, { shouldDirty, shouldValidate });
			setValue(fieldNames.city, zipCodeStore.zipCode.city as PathValue<T, Path<T>>, { shouldDirty, shouldValidate });
			setValue(fieldNames.county, zipCodeStore.zipCode.county as PathValue<T, Path<T>>, { shouldDirty, shouldValidate });
			setValue(fieldNames.stateId, zipCodeStore.zipCode.stateId as PathValue<T, Path<T>>, { shouldDirty, shouldValidate });
		}
	}, [setValue, zipCodeStore.zipCode, fieldNames, shouldDirty, shouldValidate]);
};
